import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, FileText } from "lucide-react";
import Button from "../Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import supportPrograms from "src/data/app/programs";
import * as actions from "src/redux/slices/programSlice";
import Shimmer from "src/components/common/Shimmer";
import Card from "../Card/Card";

interface DocumentStatusCardProps {
  supportProgramKey: string;
}

const DocumentStatusCard: React.FC<DocumentStatusCardProps> = ({
  supportProgramKey,
}) => {
  const navigate = useNavigate();
  const { patient } = useAppSelector((state) => state.auth);
  const { programRecords } = useAppSelector((state) => state.program);
  const dispatch = useAppDispatch();
  const [content, setContent] = useState<{
    title: string;
    description: string;
    buttonText: string;
    color: string;
    bgColor: string;
    btnColor: string;
  } | null>(null);

  const config =
    supportPrograms[supportProgramKey as keyof typeof supportPrograms];
  const requiredDocuments = config?.documents?.required_documents || [];

  // const someDocsRejected = programRecords?.data?.some(
  //   (record) => record?.record_meta_info?.report_status === "Rejected"
  // );

  useEffect(() => {
    getProgramRecords();
  }, []);

  useEffect(() => {
    if (programRecords?.data) {
      // if all documents are uploaded
      if (requiredDocuments?.length === programRecords?.data?.length) {
        const rejectedDocs = programRecords?.data?.filter(
          (record) => record?.record_meta_info?.report_status === "Rejected"
        );

        // if some documents are rejected
        if (rejectedDocs?.length > 0) {
          // if (true) {
          setContent({
            title: "Documents Rejected",
            description:
              "Some of your documents have been rejected. Please re-upload the required documents again.",
            buttonText: "Re-Upload Documents",
            color: "#FF0000",
            bgColor: "#FFE5E5",
            btnColor: "danger",
          });
        } else {
          // if all documents are uploaded and review pending
          setContent({
            title: "We are currently reviewing your documents",
            description:
              "Documents are under review. This will take up to 24 hrs.",
            buttonText: "View Documents",
            color: "#000000",
            bgColor: "var(--icon-bg-primary)",
            btnColor: "primary",
          });
        }
      } else {
        // if some documents are missing
        setContent({
          title: "Upload Missing Documents",
          description:
            "Some of the required documents are missing. Please upload the missing documents.",
          buttonText: "Upload Missing Documents",
          color: "#F59E0B",
          bgColor: "#FFF8E1",
          btnColor: "warning",
        });
      }
    } else if (!programRecords?.data && !programRecords?.isLoading) {
      // if no documents are uploaded
      setContent({
        title: "No Documents Uploaded",
        description:
          "No documents have been uploaded. Please upload the required documents.",
        buttonText: "Upload Documents",
        // Yellow color
        color: "#F59E0B",
        bgColor: "#FFF8E1",
        btnColor: "warning",
      });
    }
  }, [programRecords]);

  const getProgramRecords = () => {
    const payload = {
      patient_id: patient?.data?.patient_id,
      report_type: config?.documents?.document_type,
    };
    new Promise((resolve, reject) => {
      dispatch(actions.getProgramRecordsRequest({ resolve, reject, payload }));
    });
  };

  const handleViewDocumentsClick = () => {
    navigate(`/app/${supportProgramKey}/program-documents`);
  };

  if (programRecords?.isLoading) {
    return (
      <div className="mx-4 border border-gray-200 rounded-lg p-4 bg-white flex flex-row items-start gap-3 min-h-[130px]">
        <Shimmer width="40px" height="40px" />
        <div className="flex flex-col gap-3 flex-1">
          <Shimmer width="100%" height="16px" rounded="rounded-full" />
          <Shimmer width="100%" height="16px" rounded="rounded-full" />
          <Shimmer width="100%" height="32px" rounded="rounded-full" />
        </div>
      </div>
    );
  }

  // return (
  //   <div className="mx-4">
  //     <Card
  //       title="Documents Status"
  //       description={content?.title}
  //       icon={<Clock className="w-5 h-5" style={{ color: content?.color }} />}
  //       color={cardColorType}
  //     >
  //       <p className="text-xs leading-relaxed text-gray-500">
  //         {content?.description}
  //       </p>
  //       <Button
  //         variant="contained"
  //         rounded="full"
  //         fullWidth={true}
  //         onClick={handleViewDocumentsClick}
  //       >
  //         <div className="flex flex-row items-center justify-center gap-2">
  //           <FileText className="w-4 h-4 text-white" />
  //           <p className="text-white text-sm">{content?.buttonText}</p>
  //         </div>
  //       </Button>
  //     </Card>
  //   </div>
  // );

  return (
    <div className="mx-4 border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-start gap-3">
          <div
            className="rounded-xl p-2 w-10 h-10 flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: content?.bgColor }}
          >
            <Clock className="w-5 h-5" style={{ color: content?.color }} />
          </div>
          <div className="flex-1 space-y-2">
            <h3
              className="font-semibold text-sm mb-0.5"
              style={{ color: content?.color }}
            >
              {content?.title}
            </h3>
            <p className="text-xs leading-relaxed text-gray-500">
              {content?.description}
            </p>
            <Button
              variant="contained"
              color={content?.btnColor as any}
              rounded="full"
              fullWidth={true}
              onClick={handleViewDocumentsClick}
            >
              <div className="flex flex-row items-center justify-center gap-2">
                <FileText className="w-4 h-4 text-white" />
                <p className="text-white text-sm">{content?.buttonText}</p>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentStatusCard;
